import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import TextField from "./components/TextField";
import MessageBox from "./components/MessageBox";
import { AiOutlineLoading } from "react-icons/ai";
import PasswordField from "./components/PasswordField";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/context/AdminContext";
import { defaultImage } from "../../middlewares/libraries/imageKit/ImageKit";
import PhoneInput from "react-phone-input-2";
import { allowedCountries, countriesList } from "../home/component/data";
import { HiCheckCircle, HiXCircle } from "react-icons/hi";

const strengthSegments = 5;

const getPasswordHints = (pwd = "") => {
  const hasLen6 = pwd.length >= 6;
  const hasLen10 = pwd.length >= 10;
  const hasLower = /[a-z]/.test(pwd);
  const hasUpper = /[A-Z]/.test(pwd);
  const hasNumber = /\d/.test(pwd);
  const hasSymbol = /[^A-Za-z0-9]/.test(pwd);

  // Visual-only score: prefer diversity; clamp to 5 segments
  let score = 0;
  if (hasLen6) score += 1;
  if (hasLen10) score += 1;
  if (hasLower) score += 1;
  if (hasUpper) score += 1;
  if (hasNumber) score += 1;
  if (hasSymbol) score += 1;
  const clamped = Math.min(strengthSegments, score);

  let label = "Too weak";
  if (clamped >= 4) label = "Strong";
  else if (clamped === 3) label = "Good";
  else if (clamped === 2) label = "Fair";

  return {
    label,
    score: clamped,
    checks: {
      hasLen6,
      hasLen10,
      hasLower,
      hasUpper,
      hasNumber,
      hasSymbol,
    },
  };
};

const HintItem = ({ ok, children }) => (
  <li className="flex items-center gap-2 text-xs">
    {ok ? (
      <HiCheckCircle className="h-4 w-4 text-emerald-600" />
    ) : (
      <HiXCircle className="h-4 w-4 text-gray-300" />
    )}
    <span className={ok ? "text-gray-700" : "text-gray-500"}>{children}</span>
  </li>
);

const SignUp = () => {
  const [email, setEmail] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [phonenumber, setPhonenumber] = useState(null);
  const [country, setCountry] = useState("Uganda");
  const [countryCode, setCountryCode] = useState("ug");

  const [name, setName] = useState(null);
  const [error, setError] = useState(false);
  const [succes, setSuccess] = useState(false);
  const [password, setPassword] = useState(null);
  const [cPassword, setCPassword] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setErrorMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const { adminSignUp, sysAdmin } = useAdminContext();

  const responses = (err, message) => {
    setError(err);
    setSuccess(!err);
    setLoading(false);
    setErrorMessage(message);
  };

  const sendLoginRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);

    if (!name || name === "") {
      responses(true, "company name is required");
      return;
    } else if (!firstName || firstName === "") {
      responses(true, "first name is required");
      return;
    } else if (!lastName || lastName === "") {
      responses(true, "last name is required");
      return;
    } else if (!email || email === "") {
      responses(true, "email is required");
      return;
    } else if (!phonenumber || phonenumber === "") {
      responses(true, "phone number is required");
      return;
    } else if (!country || country === "") {
      responses(true, "country is required");
      return;
    } else if (!password || password === "") {
      responses(true, "password is required");
      return;
    } else if (String(password).length < 6) {
      responses(true, "Min. Password size is 6");
      return;
    } else if (String(password) !== String(cPassword)) {
      responses(true, "passwords do not match");
      return;
    }

    const { message, status } = await adminSignUp({
      name: name,
      thumbnail: defaultImage,
      description: name,
      email: email,
      first_name: firstName,
      last_name: lastName,
      password: password,
      phonenumber: phonenumber,
      country: country,
    });
    if (status !== 200 || status !== 201) responses(true, message);
  };

  const pwdHints = useMemo(() => getPasswordHints(password || ""), [password]);
  const passwordsMatch = (password || "") && (cPassword || "") && String(password) === String(cPassword);

  if (sysAdmin !== null) {
    return <Navigate replace to="/" />;
  }

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-white text-gray-900">
      <div className="mx-auto grid min-h-screen w-full max-w-7xl grid-cols-1 lg:grid-cols-2">
        {/* Left: Brand & Story (desktop only) */}
        <aside className="relative hidden lg:block">
          <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_-200px_-200px,rgba(16,185,129,0.08),transparent_60%)]" />
          <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
            <div className="flex items-center gap-3">
              <img src={dosee_logo} alt="Dósee Africa logo" className="h-10 w-auto" />
              <div className="text-sm">
                <p className="font-semibold">Dósee Africa • Admin</p>
                <p className="text-xs opacity-80">Create a company account to manage experiences</p>
              </div>
            </div>

            <div className="mt-12 space-y-8 pr-6">
              <h2 className="text-3xl font-bold tracking-tight">Create Your Company Account</h2>
              <p className="text-sm leading-6 text-gray-700 max-w-prose">
                Join our mission to make African tourism simple, authentic, and sustainable. Register your company to publish experiences, manage bookings, and connect with travelers—while supporting conservation and local communities.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Trusted</p>
                  <p className="mt-1 text-sm text-gray-700">Government ID & compliance checks built into our process.</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Secure</p>
                  <p className="mt-1 text-sm text-gray-700">Industry-standard encryption for accounts and payments.</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Support</p>
                  <p className="mt-1 text-sm text-gray-700">Regional teams available to help you onboard and grow.</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Sustainability</p>
                  <p className="mt-1 text-sm text-gray-700">We champion eco-friendly practices and community impact.</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs text-gray-600">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                Fast, secure signup • No setup fees
              </div>
            </div>

            <footer className="mt-10 text-xs text-gray-500">© {new Date().getFullYear()} Dósee Africa.</footer>
          </div>
        </aside>

        {/* Right: Form card */}
        <main className="relative flex items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
          <div className="relative w-full max-w-xl">
            <div className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-emerald-200/40 via-amber-200/40 to-purple-200/40 blur-2xl" aria-hidden="true" />
            <div className="relative rounded-3xl border border-gray-200 bg-white p-6 sm:p-8 shadow-xl">
              {/* Mobile logo */}
              <div className="mb-6 flex items-center justify-center lg:hidden">
                <img src={dosee_logo} alt="Dósee Africa logo" className="h-12 w-auto" />
              </div>

              {/* Header */}
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-bold tracking-tight">Account Creation</h1>
                <p className="mt-2 text-sm text-gray-700">Provide the required details to create your company account.</p>
                {/* Stepper (visual only) */}
                <div className="mt-4 flex items-center justify-center gap-3 text-[11px] text-gray-600">
                  <span className="inline-flex items-center gap-2"><span className="h-1.5 w-8 rounded-full bg-emerald-500" /> Company</span>
                  <span className="inline-flex items-center gap-2"><span className="h-1.5 w-8 rounded-full bg-emerald-400" /> Owner</span>
                  <span className="inline-flex items-center gap-2"><span className="h-1.5 w-8 rounded-full bg-emerald-300" /> Security</span>
                  <span className="inline-flex items-center gap-2"><span className="h-1.5 w-8 rounded-full bg-gray-200" /> Confirm</span>
                </div>
              </div>

              <form onSubmit={sendLoginRequest}>
                <div className="grid grid-cols-1 gap-4">
                  {/* Company */}
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">Company Name</label>
                    <TextField
                      type="text"
                      value={name}
                      title="Company Name"
                      onChange={(e) => setName(e.target.value)}
                    />
                    <p className="mt-1 text-[11px] text-gray-500">As registered legally. This appears on invoices and your public profile.</p>
                  </div>

                  {/* Owner */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">First Name</label>
                      <TextField
                        type="text"
                        value={firstName}
                        title="First Name"
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">Last Name</label>
                      <TextField
                        type="text"
                        value={lastName}
                        title="Last Name"
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">Email Address</label>
                    <TextField
                      value={email}
                      type="email"
                      title="Email Address"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <p className="mt-1 text-[11px] text-gray-500">We’ll send a verification message here to activate your account.</p>
                  </div>

                  {/* Region & Phone */}
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">Country</label>
                      <div className="relative">
                        <select
                          value={country ? country : ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== "Select Country") {
                              const pickedCountry = countriesList?.find((_c) => _c.name === val);
                              setCountry(pickedCountry.name);
                              setCountryCode(pickedCountry.code);
                            } else {
                              setCountry("Uganda");
                              setCountryCode("ug");
                            }
                          }}
                          className="block w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        >
                          <option value="Select Country">Select Country</option>
                          {countriesList?.map((ctry, index) => (
                            <option key={index} value={ctry.name}>
                              {ctry.name}
                            </option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">▾</span>
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">Choose your company’s primary operating country.</p>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-700">Telephone</label>
                      <PhoneInput
                        country={countryCode ? countryCode : ""}
                        preferredCountries={["ug", "cd", "ke"]}
                        onlyCountries={allowedCountries}
                        enableSearch={true}
                        autoFormat={true}
                        disableDropdown={true}
                        value={phonenumber}
                        enableLongNumbers={false}
                        onChange={(phone) => setPhonenumber(phone)}
                        inputStyle={{
                          width: "100%",
                          paddingTop: "10px",
                          paddingBottom: "10px",
                          height: "46px",
                          borderColor: "rgb(209,213,219)",
                          borderRadius: "0.5rem",
                          fontSize: "14px",
                        }}
                        buttonStyle={{
                          borderColor: "rgb(229,231,235)",
                          backgroundColor: "rgb(243,244,246)",
                          borderTopLeftRadius: "0.5rem",
                          borderBottomLeftRadius: "0.5rem",
                        }}
                      />
                      <p className="mt-1 text-[11px] text-gray-500">Add a number we can use for quick verification if needed.</p>
                    </div>
                  </div>

                  {/* Security */}
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-light text-gray-500">Password must be 6 or more characters.</p>
                      {password ? (
                        <span className="text-[11px] text-gray-600">Strength: <span className="font-medium">{pwdHints.label}</span></span>
                      ) : null}
                    </div>
                    <PasswordField
                      title="Password"
                      password={password}
                      setError={setError}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(false);
                        setSuccess(false);
                      }}
                    />

                    {/* Strength bar (visual only) */}
                    <div className="mt-2 flex items-center gap-1" aria-hidden="true">
                      {Array.from({ length: strengthSegments }).map((_, i) => (
                        <span
                          key={i}
                          className={`h-1.5 w-full rounded-full ${i < pwdHints.score ? "bg-emerald-500" : "bg-gray-200"}`}
                        />
                      ))}
                    </div>

                    {/* Hints */}
                    <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
                      <HintItem ok={pwdHints.checks.hasLen6}>At least 6 characters</HintItem>
                      <HintItem ok={pwdHints.checks.hasLen10}>10+ characters (recommended)</HintItem>
                      <HintItem ok={pwdHints.checks.hasLower}>Lowercase letter</HintItem>
                      <HintItem ok={pwdHints.checks.hasUpper}>Uppercase letter</HintItem>
                      <HintItem ok={pwdHints.checks.hasNumber}>Number</HintItem>
                      <HintItem ok={pwdHints.checks.hasSymbol}>Symbol</HintItem>
                    </ul>
                  </div>

                  <div>
                    <label className="sr-only">Confirm Password</label>
                    <PasswordField
                      title="Confirm Password"
                      password={cPassword}
                      setError={setError}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      onChange={(e) => {
                        setCPassword(e.target.value);
                        setError(false);
                        setSuccess(false);
                      }}
                    />
                    {cPassword ? (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        {passwordsMatch ? (
                          <HiCheckCircle className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <HiXCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className={passwordsMatch ? "text-emerald-700" : "text-red-600"}>
                          {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {/* Info Note */}
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <p className="text-xs font-normal text-gray-800 italic">
                      An email with additional steps to follow will be sent to your email address.
                    </p>
                  </div>

                  {/* Message box (same logic) */}
                  <MessageBox
                    error={error}
                    success={succes}
                    message={message}
                    setError={setError}
                    setSuccess={setSuccess}
                    setErrorMessage={setErrorMessage}
                    onClick={() => {
                      setError(false);
                      setSuccess(false);
                    }}
                  />

                  {/* Terms */}
                  <p className="text-[11px] text-gray-600">
                    By creating an account, you agree to Dósee Africa’s
                    <Link to="/terms" className="mx-1 underline">Terms of Use</Link>
                    and
                    <Link to="/privacy" className="ml-1 underline">Privacy Policy</Link>.
                  </p>
                </div>

                {/* Footer actions */}
                <div className="mt-6 flex items-center justify-between">
                  <Link
                    to="/login"
                    className="text-xs font-bold text-emerald-700 underline hover:text-emerald-600"
                  >
                    Have an account already? Login instead
                  </Link>
                </div>

                <div className="mt-4">
                  <button
                    type={loading ? "button" : "submit"}
                    className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  >
                    <span className="absolute inset-0 -z-10 bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
                    {loading ? (
                      <AiOutlineLoading className="text-lg animate-spin" />
                    ) : (
                      <span>Sign Up</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SignUp;
